import { axiosInstance } from '../lib/axios.js';

// Request deduplication cache
const pendingRequests = new Map();

// Helper function to create cache key
const getCacheKey = (method, url, config = {}) => {
  const params = config.params ? JSON.stringify(config.params) : '';
  const headers = config.headers ? JSON.stringify(config.headers) : '';
  // Include signal reference to prevent deduplication of requests with different abort signals
  const signalId = config.signal ? config.signal.toString() : 'no-signal';
  return `${method}:${url}:${params}:${headers}:${signalId}`;
};

// Helper function to deduplicate requests
const deduplicateRequest = async (method, url, data = null, config = {}) => {
  const cacheKey = getCacheKey(method, url, config);
  
  // If request is already pending, return the existing promise
  if (pendingRequests.has(cacheKey)) {
    console.log(`[API] Deduplicating ${method.toUpperCase()} request to ${url}`);
    return pendingRequests.get(cacheKey);
  }
  
  // Create new request promise
  const requestPromise = axiosInstance({
    method,
    url,
    data,
    ...config
  }).catch((error) => {
    // Remove from pending requests immediately if aborted
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      pendingRequests.delete(cacheKey);
    }
    throw error;
  }).finally(() => {
    // Remove from pending requests when complete (if not already removed)
    pendingRequests.delete(cacheKey);
  });
  
  // Store in pending requests
  pendingRequests.set(cacheKey, requestPromise);
  
  return requestPromise;
};

// Generic API functions for notifications
const api = {
  // Base HTTP methods with deduplication
  get: async (url, config = {}) => {
    console.log(`API GET call: ${url}`, config);
    try {
      // Use axiosInstance.get() directly to preserve params object for GET requests
      const response = await axiosInstance.get(url, config);
      console.log(`API GET response for ${url}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`API GET error for ${url}:`, error);
      throw error;
    }
  },

  post: async (url, data = {}, config = {}) => {
    console.log(`API POST call: ${url}`, { data, config });
    try {
      const response = await axiosInstance.post(url, data, config);
      console.log(`API POST response for ${url}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`API POST error for ${url}:`, error);
      throw error;
    }
  },

  put: async (url, data = {}, config = {}) => {
    console.log(`API PUT call: ${url}`, { data, config });
    try {
      const response = await axiosInstance.put(url, data, config);
      console.log(`API PUT response for ${url}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`API PUT error for ${url}:`, error);
      throw error;
    }
  },

  delete: async (url, config = {}) => {
    console.log(`API DELETE call: ${url}`, config);
    try {
      const response = await axiosInstance.delete(url, config);
      console.log(`API DELETE response for ${url}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`API DELETE error for ${url}:`, error);
      throw error;
    }
  },
  // Get all notifications with optional filters
  getNotifications: async (page = 1, limit = 10, filter = {}) => {
    console.log(`API call: getNotifications(page=${page}, limit=${limit}, filter=`, filter, ')');
    const params = { page, limit, ...filter };
    try {
      const response = await axiosInstance.get('/notifications', { params });
      console.log('getNotifications response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getNotifications error:', error);
      throw error;
    }
  },

  // Get unread notification count with retry logic
  getUnreadCount: async (filter = {}, retries = 2) => {
    console.log('API call: getUnreadCount()');
    try {
      const params = {};
      if (filter.type && filter.type !== 'all') {
        params.type = filter.type;
      }
      if (filter.leagueSlug) {
        params.leagueSlug = filter.leagueSlug;
      }
      const response = await axiosInstance.get('/notifications/unread-count', { params });
      console.log('getUnreadCount response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getUnreadCount error:', error);
      
      // Retry logic for network errors
      if (retries > 0 && (error.code === 'ERR_NETWORK' || !error.response)) {
        console.log(`Network error in getUnreadCount, retrying... (${retries} attempts left)`);
        // Wait before retrying with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, 2 - retries), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
        // Use the module's function directly instead of 'this'
        return api.getUnreadCount(filter, retries - 1);
      }
      
      throw error;
    }
  },

  // Get total notification count with retry logic
  getTotalCount: async (filter = {}, retries = 2) => {
    console.log('API call: getTotalCount()');
    try {
      const params = {};
      if (filter.type && filter.type !== 'all') {
        params.type = filter.type;
      }
      if (filter.leagueSlug) {
        params.leagueSlug = filter.leagueSlug;
      }
      const response = await axiosInstance.get('/notifications/count', { params });
      console.log('getTotalCount response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getTotalCount error:', error);
      
      // Retry logic for network errors
      if (retries > 0 && (error.code === 'ERR_NETWORK' || !error.response)) {
        console.log(`Network error in getTotalCount, retrying... (${retries} attempts left)`);
        // Wait before retrying with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, 2 - retries), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
        // Use the module's function directly instead of 'this'
        return api.getTotalCount(filter, retries - 1);
      }
      
      throw error;
    }
  },

  // Mark notification as read
  markAsRead: async (notificationId) => {
    console.log(`API call: markAsRead(${notificationId})`);
    try {
      const response = await axiosInstance.put(`/notifications/${notificationId}/read`);
      console.log('markAsRead response:', response.data);
      return response.data;
    } catch (error) {
      console.error('markAsRead error:', error);
      throw error;
    }
  },

  // Mark all notifications as read
  markAllAsRead: async () => {
    console.log('API call: markAllAsRead()');
    try {
      const response = await axiosInstance.post('/notifications/read-all');
      console.log('markAllAsRead response:', response.data);
      return response.data;
    } catch (error) {
      console.error('markAllAsRead error:', error);
      throw error;
    }
  },

  // Delete notification
  deleteNotification: async (notificationId) => {
    console.log(`API call: deleteNotification(${notificationId})`);
    try {
      const response = await axiosInstance.delete(`/notifications/${notificationId}`);
      console.log('deleteNotification response:', response.data);
      return response.data;
    } catch (error) {
      console.error('deleteNotification error:', error);
      throw error;
    }
  }
};

export default api;
